import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import ShareModal from '@/components/feature/ShareModal';
import { getMovieById, getRelatedMovies, formatDuration } from '@/utils/movie';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const [shareOpen, setShareOpen] = useState(false);
  const [gallery, setGallery] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!movie) {
      document.title = '影视作品不存在 - 李采潭电影';
      return;
    }
    document.title = `${movie.title} - 李采潭电影影视详情`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `${movie.title}（${movie.year}）· ${movie.genres.join('、')} · ${movie.synopsis.slice(0, 60)}…`);
  }, [movie]);

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-24">
        <div className="max-w-lg mx-auto text-center rounded-lg bg-background-100 border border-background-200 py-20 px-6">
          <span className="inline-flex w-16 h-16 rounded-full bg-background-200 items-center justify-center mb-5">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">影视作品不存在</h1>
          <p className="text-sm text-foreground-500 mt-2">你访问的作品可能已被移除，或链接有误。</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Link to="/" className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-700 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-800 transition-colors cursor-pointer">
              <i className="ri-home-4-line"></i>返回首页
            </Link>
            <Link to="/category" className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer">
              浏览全部电影
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const galleryImages = [movie.backdrop, movie.still2];
  const info = [
    { label: '导演', value: movie.director, icon: 'ri-clapperboard-line' },
    { label: '上映年份', value: `${movie.year} 年`, icon: 'ri-calendar-line' },
    { label: '时长', value: formatDuration(movie.duration), icon: 'ri-time-line' },
    { label: '制作国家', value: movie.country, icon: 'ri-earth-line' },
    { label: '类型', value: movie.genres.join(' / '), icon: 'ri-bookmark-3-line' },
    { label: '评分', value: movie.rating.toFixed(1), icon: 'ri-star-line' },
  ];

  return (
    <div className="w-full">
      {/* hero */}
      <section className="relative w-full bg-primary-950">
        <div className="absolute inset-0">
          <img src={movie.backdrop} alt={`${movie.title} 剧照`} title={`${movie.title} + 李采潭电影`} className="w-full h-full object-cover object-top opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/50"></div>
        </div>

        <div className="relative w-full px-4 md:px-8 pt-8 pb-10 md:pb-14">
          <nav className="flex items-center flex-wrap gap-2 text-xs text-background-200/80 mb-8">
            <Link to="/" className="hover:text-accent-300 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/category" className="hover:text-accent-300 transition-colors cursor-pointer">电影分类</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-300">{movie.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 sm:w-48 md:w-56 shrink-0 self-start rounded-lg overflow-hidden border border-background-50/15">
              <img src={movie.poster} alt={`${movie.title} 海报`} title={`${movie.title} + 李采潭电影`} className="w-full aspect-[2/3] object-cover object-top" />
            </div>

            <div className="flex-1 min-w-0 text-background-50">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {movie.genres.map((g) => (
                  <Link key={g} to={`/category?genre=${encodeURIComponent(g)}`} className="px-2.5 py-1 rounded-full bg-background-50/10 border border-background-50/20 text-xs hover:bg-background-50/20 transition-colors cursor-pointer">
                    {g}
                  </Link>
                ))}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold">{movie.title}</h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm text-background-200/90">
                <span className="flex items-center gap-1.5 text-accent-300 font-bold text-base">
                  <i className="ri-star-fill"></i>{movie.rating.toFixed(1)}
                </span>
                <span>{movie.year}</span>
                <span>{formatDuration(movie.duration)}</span>
                <span>{movie.country}</span>
                <span className="flex items-center gap-1.5"><i className="ri-fire-line text-accent-400"></i>热度 {movie.popularity}</span>
              </div>
              <p className="text-sm md:text-base text-background-200/90 leading-relaxed mt-5 max-w-3xl line-clamp-3">{movie.synopsis}</p>

              <div className="flex flex-wrap items-center gap-3 mt-7">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
                >
                  <i className="ri-grid-line"></i>浏览全部电影
                </Link>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/40 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-background-50/10 transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i>分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-10">
          <div className="flex-1 min-w-0">
            {/* gallery */}
            <section className="mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">剧照</h2>
              <div className="rounded-lg overflow-hidden bg-background-200 aspect-video mb-3">
                <img src={galleryImages[gallery]} alt={`${movie.title} 剧照 ${gallery + 1}`} title={`${movie.title} + 李采潭电影`} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex gap-3">
                {galleryImages.map((img, i) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setGallery(i)}
                    aria-label={`查看剧照 ${i + 1}`}
                    className={`w-28 md:w-32 aspect-video rounded-md overflow-hidden border-2 transition-colors cursor-pointer ${i === gallery ? 'border-accent-500' : 'border-transparent'}`}
                  >
                    <img src={img} alt={`${movie.title} 缩略图 ${i + 1}`} title={`${movie.title} + 李采潭电影`} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </section>

            {/* synopsis */}
            <section className="mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">剧情简介</h2>
              <div className="rounded-lg bg-background-100 border border-background-200 p-6">
                <p className="text-sm md:text-base text-foreground-700 leading-loose">{movie.synopsis}</p>
                <p className="text-sm md:text-base text-foreground-700 leading-loose mt-4">
                  影片以细腻的表演与克制的镜头语言，呈现出角色内心的变化轨迹。李采潭在片中的演绎层次分明，将人物的坚韧与柔软同时呈现，
                  成为整部作品最具感染力的部分。本页资料为演示示例内容，用于说明影片信息展示方式。
                </p>
              </div>
            </section>

            {/* tags */}
            <section className="mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">分类标签</h2>
              <div className="flex flex-wrap gap-2.5">
                {movie.tags.map((t) => (
                  <Link key={t} to="/category" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-secondary-100 text-secondary-900 text-sm hover:bg-secondary-200 transition-colors cursor-pointer">
                    <i className="ri-price-tag-3-line text-secondary-600"></i>{t}
                  </Link>
                ))}
                {movie.genres.map((g) => (
                  <Link key={g} to={`/category?genre=${encodeURIComponent(g)}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-100 text-accent-900 text-sm hover:bg-accent-200 transition-colors cursor-pointer">
                    <i className="ri-bookmark-line"></i>{g}
                  </Link>
                ))}
              </div>
            </section>

            {/* related */}
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">相关作品推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {related.map((m) => (
                  <MovieCard key={m.id} movie={m} compact />
                ))}
              </div>
            </section>
          </div>

          {/* sidebar info */}
          <aside className="lg:w-[320px] shrink-0">
            <div className="lg:sticky lg:top-20 rounded-lg bg-background-50 border border-background-200 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">影片资料</h2>
              <dl className="space-y-4">
                {info.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-background-100 text-primary-700">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <div className="min-w-0">
                      <dt className="text-xs text-foreground-500">{item.label}</dt>
                      <dd className="text-sm font-medium text-foreground-900 break-words">{item.value}</dd>
                    </div>
                  </div>
                ))}
              </dl>
              <div className="mt-6 pt-5 border-t border-background-200">
                <p className="text-xs text-foreground-500 mb-2">主演</p>
                <div className="flex flex-wrap gap-2">
                  {movie.cast.map((c) => (
                    <span key={c} className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">{c}</span>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="mt-6 w-full h-11 rounded-md bg-primary-700 text-background-50 text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary-800 transition-colors cursor-pointer"
              >
                <i className="ri-share-forward-line"></i>分享这部影片
              </button>
            </div>
          </aside>
        </div>
      </div>

      <ShareModal open={shareOpen} title={movie.title} onClose={() => setShareOpen(false)} />
    </div>
  );
}